import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, ProgressBar, Badge, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';
import CTABannerUpload from '../../components/CTABannerUpload';

// CSS for always-visible scrollbar
const scrollbarStyles = `
  .always-show-scrollbar::-webkit-scrollbar {
    width: 8px;
    display: block;
  }
  .always-show-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .always-show-scrollbar::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 4px;
  }
  .always-show-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #6c757d;
  }
  .always-show-scrollbar {
    overflow-y: scroll !important;
  }
`;

function OnboardingStatus() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [preferences, setPreferences] = useState({
    reminder_sms: false,
    reminder_email: true,
    phone_number: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    reminder_hour: '9',
    reminder_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    quiet_mode: false,
  });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [siteKey, setSiteKey] = useState(null);
  const [siteConnected, setSiteConnected] = useState(false);
  const [siteUrl, setSiteUrl] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Elementor template settings
  const [elementorSettings, setElementorSettings] = useState({
    blog_template_id: '',
    page_template_id: ''
  });
  const [elementorTemplates, setElementorTemplates] = useState([]);
  const [elementorActive, setElementorActive] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [elementorSaving, setElementorSaving] = useState(false);
  const [elementorSaved, setElementorSaved] = useState(false);

  // CTA Banner settings
  const [ctaBanners, setCtaBanners] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [autoPushingBanners, setAutoPushingBanners] = useState(false);

  // Sales page decision flow for CTA banners
  const [existingSalesPageUrl, setExistingSalesPageUrl] = useState(null);
  const [existingLeadMagnetUrl, setExistingLeadMagnetUrl] = useState(null);
  const [salesPageDecision, setSalesPageDecision] = useState(null); // null, 'keep', 'new'
  const [newPageType, setNewPageType] = useState(null); // null, 'sales_page', 'funnel'

  // GHL account info
  const [ghlStatus, setGhlStatus] = useState(null);

  const navigate = useNavigate();

  // Map onboarding item IDs to anchor IDs on this page
  const platformAnchorMap = {
    wordpress: 'section-wordpress',
    cta_banners: 'section-cta-banners',
    notifications: 'section-notifications',
    socials: 'section-ghl-dashboard',
    stripe: 'section-ghl-dashboard',
    domain: 'section-ghl-dashboard',
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-push banners when WordPress gets connected
  useEffect(() => {
    const autoPushBanners = async () => {
      // Find banners that are uploaded but not pushed to WordPress
      const unpushedBanners = ctaBanners.filter(b => b.id && !b.wp_media_url);

      if (siteConnected && unpushedBanners.length > 0 && !autoPushingBanners) {
        setAutoPushingBanners(true);
        console.log(`[CTA] Auto-pushing ${unpushedBanners.length} banner(s) to WordPress...`);

        for (const banner of unpushedBanners) {
          try {
            const response = await fetch(`/api/cta/banners/${banner.id}/push`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
              console.log(`[CTA] Auto-pushed banner ${banner.id} successfully`);
            } else {
              console.error(`[CTA] Failed to auto-push banner ${banner.id}`);
            }
          } catch (err) {
            console.error(`[CTA] Error auto-pushing banner ${banner.id}:`, err);
          }
        }

        // Refresh banners list to get updated wp_media_url values
        fetchCtaBanners();
        setAutoPushingBanners(false);
      }
    };

    autoPushBanners();
  }, [siteConnected, ctaBanners]);

  const fetchData = async () => {
    try {
      const [statusRes, prefsRes, siteKeyRes, foundationRes] = await Promise.all([
        fetch('/api/onboarding/status', { credentials: 'include' }),
        fetch('/api/preferences', { credentials: 'include' }),
        fetch('/api/settings/site-key', { credentials: 'include' }),
        fetch('/api/foundation/complete', { credentials: 'include' })
      ]);

      if (statusRes.ok) {
        const data = await statusRes.json();
        setStatus(data);
      } else {
        throw new Error('Failed to fetch status');
      }

      if (prefsRes.ok) {
        const data = await prefsRes.json();
        if (data.preferences) {
          setPreferences({
            reminder_sms: data.preferences.reminder_sms || false,
            reminder_email: data.preferences.reminder_email !== false,
            phone_number: data.preferences.phone_number || '',
            timezone: data.preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
            reminder_hour: data.preferences.reminder_hour || '9',
            reminder_days: data.preferences.reminder_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            quiet_mode: data.preferences.quiet_mode || false,
          });
        }
      }

      if (siteKeyRes.ok) {
        const data = await siteKeyRes.json();
        setSiteKey(data.site_key);
        setSiteConnected(!!data.site_url);
        setSiteUrl(data.site_url);

        // Always fetch CTA banners (can upload before WordPress connection)
        fetchCtaBanners();

        // Only fetch Elementor settings if site is connected
        if (data.site_url) {
          fetchElementorSettings();
        }
      }

      // Fetch GHL status
      try {
        const ghlRes = await fetch('/api/ghl/status', { credentials: 'include' });
        if (ghlRes.ok) {
          const ghlData = await ghlRes.json();
          setGhlStatus(ghlData);
        }
      } catch (ghlErr) {
        console.error('Error fetching GHL status:', ghlErr);
      }

      // Get existing sales page URL and lead magnet URL from foundation data
      if (foundationRes.ok) {
        const data = await foundationRes.json();
        // Check both 'sales_page_url' (from AnalyzeBusiness/publish) and 'sales_page' (from other sources)
        const salesUrl = data.foundation?.sales_page_url || data.foundation?.sales_page;
        if (salesUrl) {
          setExistingSalesPageUrl(salesUrl);
        }
        // Get lead magnet URL (from funnel publish)
        const leadMagnetUrl = data.foundation?.lead_magnet_url;
        if (leadMagnetUrl) {
          setExistingLeadMagnetUrl(leadMagnetUrl);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setPrefsSaving(true);
    setPrefsSaved(false);
    try {
      const response = await fetch('/api/preferences', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences)
      });
      if (response.ok) {
        setPrefsSaved(true);
        setTimeout(() => setPrefsSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
    } finally {
      setPrefsSaving(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(siteKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = siteKey;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const fetchElementorSettings = async () => {
    try {
      const res = await fetch('/api/settings/elementor', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setElementorSettings({
          blog_template_id: data.blog_template_id || '',
          page_template_id: data.page_template_id || ''
        });
        // If we have saved template IDs, also fetch the templates list so dropdown shows selection
        if (data.blog_template_id || data.page_template_id) {
          fetchElementorTemplates();
        }
      }
    } catch (err) {
      console.error('Error fetching Elementor settings:', err);
    }
  };

  const fetchCtaBanners = async () => {
    setLoadingBanners(true);
    try {
      const res = await fetch('/api/cta/banners', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCtaBanners(data.banners || []);
      }
    } catch (err) {
      console.error('Error fetching CTA banners:', err);
    } finally {
      setLoadingBanners(false);
    }
  };

  const getExistingBanner = (bannerType) => {
    return ctaBanners.find(b => b.banner_type === bannerType);
  };

  const handleBannerUpdated = (updatedBanner) => {
    // Refresh the banners list
    fetchCtaBanners();
  };

  const fetchElementorTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await fetch('/api/settings/elementor/templates', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setElementorActive(data.elementor_active);
        setElementorTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Error fetching Elementor templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveElementorSettings = async () => {
    setElementorSaving(true);
    setElementorSaved(false);
    try {
      const res = await fetch('/api/settings/elementor', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blog_template_id: elementorSettings.blog_template_id || null,
          page_template_id: elementorSettings.page_template_id || null
        })
      });
      if (res.ok) {
        setElementorSaved(true);
        setTimeout(() => setElementorSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving Elementor settings:', err);
    } finally {
      setElementorSaving(false);
    }
  };

  const getProgressPercent = (complete, total) => {
    return Math.round((complete / total) * 100);
  };

  const groupByModule = (items) => {
    const grouped = {};
    items.forEach(item => {
      if (!grouped[item.module]) {
        grouped[item.module] = [];
      }
      grouped[item.module].push(item);
    });
    return grouped;
  };

  const handleItemClick = (item) => {
    if (item.complete) return;
    const anchorId = platformAnchorMap[item.id];
    if (anchorId) {
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Brief highlight flash
        el.style.transition = 'box-shadow 0.3s';
        el.style.boxShadow = `0 0 0 3px ${colors.goldMid}`;
        setTimeout(() => { el.style.boxShadow = 'none'; }, 2000);
        return;
      }
    }
    navigate(item.path);
  };

  const renderChecklistItem = (item) => {
    const isCriticalIncomplete = !item.complete && item.priority === 'critical';
    return (
      <div
        key={item.id}
        onClick={() => handleItemClick(item)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          borderBottom: `1px solid ${colors.greyLight}`,
          borderLeft: isCriticalIncomplete ? '3px solid #dc3545' : '3px solid transparent',
          cursor: item.complete ? 'default' : 'pointer',
          backgroundColor: item.complete ? '#f8fff8' : isCriticalIncomplete ? '#fff5f5' : 'white',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!item.complete) {
            e.currentTarget.style.backgroundColor = isCriticalIncomplete ? '#ffe0e0' : '#fff8e6';
          }
        }}
        onMouseLeave={(e) => {
          if (!item.complete) {
            e.currentTarget.style.backgroundColor = isCriticalIncomplete ? '#fff5f5' : 'white';
          }
        }}
      >
        <span style={{
          fontSize: '1.25rem',
          marginRight: '0.75rem',
          color: item.complete ? '#28a745' : isCriticalIncomplete ? '#dc3545' : colors.greyMid,
        }}>
          {item.complete ? '✓' : '○'}
        </span>
        <span style={{
          flex: 1,
          color: item.complete ? colors.greyDark : isCriticalIncomplete ? '#dc3545' : colors.greyDarkest,
          fontWeight: isCriticalIncomplete ? 600 : 'normal',
        }}>
          {item.name}
        </span>
        {isCriticalIncomplete && (
          <Badge bg="danger" style={{ fontSize: '0.65rem', marginRight: 8 }}>Critical</Badge>
        )}
        {!item.complete && (
          <span style={{ color: isCriticalIncomplete ? '#dc3545' : colors.goldDark, fontSize: '0.85rem' }}>
            Complete →
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Checking your progress...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">Error loading status: {error}</Alert>
      </Container>
    );
  }

  const progressPercent = getProgressPercent(status.summary.complete, status.summary.total);
  const requiredByModule = groupByModule(status.required);
  const moduleLinks = {
    'Foundation': '/dashboard/foundation',
    'Business Model': '/dashboard/business-model',
    'Brand Identity': '/dashboard/branding',
    'Growth & Marketing': '/dashboard/marketing-strategy',
  };

  return (
    <Container className="py-4">
      <style>{scrollbarStyles}</style>
      {/* Header */}
      <div className="mb-4">
        <h1>Onboarding Status</h1>
        <p className="text-muted">
          Complete these sections to unlock the full power of your Command Center.
        </p>
      </div>

      {/* Progress Summary */}
      <Card className="shadow-sm mb-4" style={{ borderLeft: `4px solid ${status.ready_to_proceed ? '#28a745' : colors.goldMid}` }}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h5 className="mb-1">Overall Progress</h5>
              <small className="text-muted">Complete all sections to unlock your full Command Center</small>
            </div>
            <Badge bg={status.ready_to_proceed ? 'success' : 'warning'} className="fs-5">
              {status.summary.complete} / {status.summary.total}
            </Badge>
          </div>
          <ProgressBar
            now={progressPercent}
            variant={status.ready_to_proceed ? 'success' : 'warning'}
            style={{ height: '12px' }}
          />
          {status.ready_to_proceed ? (
            <div className="mt-2 text-success">
              <strong>✓ All sections complete! Your Command Center is fully powered.</strong>
            </div>
          ) : (
            <>
              <div className="mt-2 text-muted">
                <strong>{status.summary.total - status.summary.complete} sections remaining</strong> - Click any incomplete item below to continue.
              </div>
              {status.priority_counts && (
                <div className="d-flex gap-3 mt-2">
                  {status.priority_counts.critical > 0 && (
                    <span style={{ fontSize: '0.85rem', color: '#dc3545', fontWeight: 600 }}>
                      {status.priority_counts.critical} Critical
                    </span>
                  )}
                  {status.priority_counts.important > 0 && (
                    <span style={{ fontSize: '0.85rem', color: '#f57c00', fontWeight: 600 }}>
                      {status.priority_counts.important} Important
                    </span>
                  )}
                  {status.priority_counts.optional > 0 && (
                    <span style={{ fontSize: '0.85rem', color: colors.greyMid, fontWeight: 600 }}>
                      {status.priority_counts.optional} Nice to Have
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Sections by Module */}
      <h4 className="mb-3" style={{ color: colors.greyDarkest }}>
        Sections to Complete
      </h4>
      <Row className="mb-4">
        {Object.entries(requiredByModule).map(([moduleName, items]) => (
          <Col lg={6} key={moduleName} className="mb-3">
            <Card className="shadow-sm h-100">
              <Card.Header style={{ backgroundColor: colors.greyLightest, borderBottom: `2px solid ${colors.goldMid}` }}>
                <strong
                  style={{
                    cursor: moduleLinks[moduleName] ? 'pointer' : 'default',
                    textDecoration: moduleLinks[moduleName] ? 'none' : 'none',
                  }}
                  onClick={() => moduleLinks[moduleName] && navigate(moduleLinks[moduleName])}
                  onMouseEnter={(e) => { if (moduleLinks[moduleName]) e.target.style.color = colors.goldDark; }}
                  onMouseLeave={(e) => { e.target.style.color = ''; }}
                  title={moduleLinks[moduleName] ? `Go to ${moduleName}` : ''}
                >
                  {moduleName}{moduleLinks[moduleName] ? ' →' : ''}
                </strong>
                <Badge
                  className="float-end"
                  bg={items.every(i => i.complete) ? 'success' : 'secondary'}
                >
                  {items.filter(i => i.complete).length} / {items.length}
                </Badge>
              </Card.Header>
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                // Always show scrollbar
                scrollbarWidth: 'thin', // Firefox
                scrollbarColor: `${colors.greyMid} ${colors.greyLightest}`, // Firefox
              }} className="always-show-scrollbar">
                {items.map(renderChecklistItem)}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Call to Action */}
      {!status.ready_to_proceed && (
        <Card className="shadow-sm" style={{ backgroundColor: '#fff8e6', borderColor: colors.goldMid }}>
          <Card.Body className="text-center py-4">
            <h5 style={{ color: colors.greyDarkest }}>
              Complete the required sections to unlock your Command Center
            </h5>
            <p className="text-muted mb-0">
              Click any incomplete item above to go directly to that section.
            </p>
          </Card.Body>
        </Card>
      )}

      {status.ready_to_proceed && (
        <Card className="shadow-sm" style={{ backgroundColor: '#f0fff4', borderColor: '#28a745' }}>
          <Card.Body className="text-center py-4">
            <h5 style={{ color: '#28a745' }}>
              You're all set! Your Command Center is fully powered.
            </h5>
            <p className="text-muted mb-0">
              All sections are complete. You're ready to generate high-converting marketing.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Platform Connections */}
      <h4 className="mb-3 mt-4" style={{ color: colors.greyDarkest }}>
        Platform Connections
      </h4>
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: colors.greyLightest, borderBottom: `2px solid ${colors.goldMid}` }}>
          <strong>Connect Your Accounts</strong>
          <Badge className="float-end" bg="secondary">Required for Launch</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {/* WordPress Plugin - Top Priority */}
          <div
            id="section-wordpress"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '1rem',
              borderBottom: siteConnected ? `1px solid ${colors.greyLight}` : `2px solid ${colors.goldMid}`,
              backgroundColor: '#fff8e6',
              scrollMarginTop: '80px',
            }}
          >
            <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>🔌</span>
            <div style={{ flex: 1 }}>
              <strong>Connect Your WordPress Site</strong>
              <p className="mb-1 text-muted" style={{ fontSize: '0.85rem' }}>
                Publish sales pages, lead magnets, and blog posts directly to your site — and automatically install visitor tracking
              </p>
              {siteConnected ? (
                <small className="text-success">
                  Connected to: {siteUrl}
                </small>
              ) : (
                <div className="mt-2">
                  <small className="text-muted d-block mb-2">
                    <strong>Step 1:</strong> Download and install the plugin, then go to <strong>Settings → AI ProfitEngineer</strong> in your WordPress admin.
                  </small>
                  <small className="text-muted d-block mb-1"><strong>Step 2:</strong> Paste this Site Key:</small>
                  <div className="d-flex align-items-center gap-2">
                    <code style={{
                      fontSize: '0.8rem',
                      backgroundColor: '#fff',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      wordBreak: 'break-all'
                    }}>
                      {siteKey || 'Loading...'}
                    </code>
                    {siteKey && (
                      <Button size="sm" variant="outline-secondary" onClick={copyKey}>
                        {keyCopied ? '✓ Copied!' : 'Copy'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="d-flex flex-column gap-2 ms-3">
              {siteConnected ? (
                <Badge bg="success">Connected</Badge>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => {
                      fetch('/api/settings/plugin/download', { credentials: 'include' })
                        .then(res => res.blob())
                        .then(blob => {
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'profitengine-connector.zip';
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                        });
                    }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Download Plugin
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Elementor Template Settings - Only show when WordPress is connected */}
          {siteConnected && (
            <div
              style={{
                padding: '1rem',
                borderBottom: `2px solid ${colors.goldMid}`,
                backgroundColor: '#f8f9fa',
              }}
            >
              <div className="d-flex align-items-start">
                <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>🎨</span>
                <div style={{ flex: 1 }}>
                  <strong>Elementor Template Settings</strong>
                  <p className="mb-2 text-muted" style={{ fontSize: '0.85rem' }}>
                    Configure Elementor for published content (optional)
                  </p>

                  {!elementorTemplates.length && !loadingTemplates ? (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={fetchElementorTemplates}
                      disabled={loadingTemplates}
                    >
                      {loadingTemplates ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Loading Templates...
                        </>
                      ) : (
                        'Load Elementor Templates'
                      )}
                    </Button>
                  ) : loadingTemplates ? (
                    <div className="d-flex align-items-center text-muted">
                      <Spinner size="sm" className="me-2" />
                      Loading templates from your WordPress site...
                    </div>
                  ) : !elementorActive ? (
                    <Alert variant="warning" className="mb-0 py-2">
                      <small>
                        Elementor is not active on your WordPress site. Install and activate Elementor to use template features.
                      </small>
                    </Alert>
                  ) : (
                    <div>
                      <Alert variant="info" className="py-2 mb-2">
                        <small>
                          <strong>For Blog Posts:</strong> Use Elementor Theme Builder to create a "Single Post" template
                          with a Post Content widget. This auto-applies to all posts.
                        </small>
                      </Alert>
                      <Row className="mb-2">
                        <Col md={12}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1" style={{ fontSize: '0.85rem' }}>
                              Page Template (for sales pages, landing pages)
                            </Form.Label>
                            <Form.Select
                              size="sm"
                              value={String(elementorSettings.page_template_id || '')}
                              onChange={(e) => setElementorSettings({
                                ...elementorSettings,
                                page_template_id: e.target.value
                              })}
                            >
                              <option value="">Use WordPress default</option>
                              {elementorTemplates.map(tpl => (
                                <option key={tpl.id} value={String(tpl.id)}>
                                  {tpl.title} (ID: {tpl.id})
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          size="sm"
                          variant="dark"
                          onClick={saveElementorSettings}
                          disabled={elementorSaving}
                          style={{ backgroundColor: colors.greyDarkest }}
                        >
                          {elementorSaving ? 'Saving...' : 'Save Templates'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={fetchElementorTemplates}
                          disabled={loadingTemplates}
                        >
                          Refresh
                        </Button>
                        {elementorSaved && (
                          <span className="text-success" style={{ fontSize: '0.85rem' }}>
                            ✓ Saved!
                          </span>
                        )}
                      </div>
                      <Form.Text className="text-muted d-block mt-2">
                        These templates will be applied when publishing content from AI Profit Engineer to WordPress.
                      </Form.Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* CTA Banners - Available before WordPress connection */}
          <div
            id="section-cta-banners"
            style={{
              padding: '1rem',
              borderBottom: `2px solid ${colors.goldMid}`,
              backgroundColor: '#f8f9fa',
              scrollMarginTop: '80px',
            }}
          >
            <div className="d-flex align-items-start">
              <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>🎯</span>
              <div style={{ flex: 1 }}>
                <strong>CTA Banners for Blog Posts</strong>
                <span className="ms-2"><Badge bg="secondary">Optional</Badge></span>
                <p className="mb-2 text-muted" style={{ fontSize: '0.85rem' }}>
                  Upload banners that appear at the bottom of your blog posts to drive traffic to your offers.
                </p>

                <Alert variant="light" className="py-2 mb-3" style={{ border: `1px solid ${colors.goldMid}`, backgroundColor: '#fff8e6' }}>
                  <small>
                    <strong>Need a starting point?</strong> Use our{' '}
                    <a
                      href="https://www.canva.com/design/DAHARhM7834/x8XhcaaaHgkFm0hFFEPrQQ/edit?utm_content=DAHARhM7834&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: colors.goldDark, fontWeight: 'bold' }}
                    >
                      Canva CTA Banner Template
                    </a>{' '}
                    to create your own. Size: <strong>800px wide x 150px tall</strong>.
                  </small>
                </Alert>

                {/* Sales Page Decision Flow - only show if user has existing sales page */}
                {existingSalesPageUrl && salesPageDecision === null && (
                  <Card className="mb-3" style={{ border: `2px solid ${colors.goldMid}`, backgroundColor: '#fffbf0' }}>
                    <Card.Body className="py-3">
                      <div className="mb-2">
                        <strong>We found your existing sales page:</strong>
                        <br />
                        <a href={existingSalesPageUrl} target="_blank" rel="noopener noreferrer" className="text-break">
                          {existingSalesPageUrl}
                        </a>
                      </div>
                      <p className="mb-3" style={{ fontSize: '0.95rem' }}>
                        Do you want or need a new sales page for your core product?
                      </p>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="outline-success"
                          onClick={() => setSalesPageDecision('keep')}
                        >
                          No, keep my current page
                        </Button>
                        <Button
                          variant="outline-primary"
                          onClick={() => setSalesPageDecision('new')}
                        >
                          Yes, I need a new one
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Show choice between Sales Page and Funnel if they want new */}
                {salesPageDecision === 'new' && newPageType === null && (
                  <Card className="mb-3" style={{ border: `2px solid ${colors.goldMid}`, backgroundColor: '#fffbf0' }}>
                    <Card.Body className="py-3">
                      <p className="mb-3" style={{ fontSize: '0.95rem' }}>
                        <strong>What would you like to create?</strong>
                      </p>
                      <Row>
                        <Col md={6} className="mb-2 mb-md-0">
                          <Card
                            onClick={() => navigate('/command-center/products/sales-pages')}
                            style={{ cursor: 'pointer', border: `1px solid ${colors.greyLight}` }}
                            className="h-100"
                          >
                            <Card.Body className="text-center py-4">
                              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</div>
                              <strong>Sales Page</strong>
                              <p className="text-muted small mb-0 mt-1">
                                A standalone page to sell your core product
                              </p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card
                            onClick={() => navigate('/command-center/campaign-hq/funnel-management')}
                            style={{ cursor: 'pointer', border: `1px solid ${colors.greyLight}` }}
                            className="h-100"
                          >
                            <Card.Body className="text-center py-4">
                              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎯</div>
                              <strong>Complete Funnel</strong>
                              <p className="text-muted small mb-0 mt-1">
                                Sales page + thank you pages + email sequences
                              </p>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-2 p-0"
                        onClick={() => setSalesPageDecision(null)}
                      >
                        ← Go back
                      </Button>
                    </Card.Body>
                  </Card>
                )}

                {/* Show confirmation when they chose to keep existing page */}
                {salesPageDecision === 'keep' && (
                  <Alert variant="success" className="py-2 mb-3">
                    <small>
                      <strong>Using your existing sales page:</strong>{' '}
                      <a href={existingSalesPageUrl} target="_blank" rel="noopener noreferrer">
                        {existingSalesPageUrl}
                      </a>
                      <Button
                        variant="link"
                        size="sm"
                        className="ms-2 p-0"
                        onClick={() => setSalesPageDecision(null)}
                      >
                        Change
                      </Button>
                    </small>
                  </Alert>
                )}

                {loadingBanners || autoPushingBanners ? (
                  <div className="d-flex align-items-center text-muted">
                    <Spinner size="sm" className="me-2" />
                    {autoPushingBanners ? 'Pushing banners to WordPress...' : 'Loading your banners...'}
                  </div>
                ) : (
                  <Row>
                    <Col lg={6}>
                      <CTABannerUpload
                        bannerType="lead_magnet"
                        title="Lead Magnet CTA"
                        description="Promote your free lead magnet (e.g., ebook, checklist)"
                        existingBanner={getExistingBanner('lead_magnet')}
                        onBannerUpdated={handleBannerUpdated}
                        siteConnected={siteConnected}
                        defaultDestinationUrl={existingLeadMagnetUrl}
                      />
                    </Col>
                    <Col lg={6}>
                      <CTABannerUpload
                        bannerType="product"
                        title="Core Product CTA"
                        description="Promote your main product or service"
                        existingBanner={getExistingBanner('product')}
                        onBannerUpdated={handleBannerUpdated}
                        siteConnected={siteConnected}
                        defaultDestinationUrl={salesPageDecision !== 'new' ? existingSalesPageUrl : null}
                      />
                    </Col>
                  </Row>
                )}

                <Alert variant="info" className="py-2 mt-2 mb-0">
                  <small>
                    <strong>Tip:</strong> Recommended banner size is <strong>800x150px</strong>.
                    {siteConnected ? (
                      <> One of your active banners will be randomly selected for each published blog post.</>
                    ) : (
                      <> Upload your banners now - you can push them to WordPress after connecting your site.</>
                    )}
                  </small>
                </Alert>
              </div>
            </div>
          </div>

        </Card.Body>
      </Card>

      {/* GoHighLevel Dashboard Access */}
      <h4 id="section-ghl-dashboard" className="mb-3 mt-4" style={{ color: colors.greyDarkest, scrollMarginTop: '80px' }}>
        GoHighLevel Dashboard
      </h4>
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <strong>Your GoHighLevel Account</strong>
        </Card.Header>
        <Card.Body>
          {ghlStatus?.provisioned ? (
            <>
              <p className="mb-3">
                Your GoHighLevel sub-account is active. Use it to manage Stripe payments, connect your domain,
                link social accounts, and set up automations.
              </p>
              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '1rem',
                border: `1px solid ${colors.greyLight}`,
                marginBottom: '1rem'
              }}>
                {ghlStatus.location_name && (
                  <div className="mb-2">
                    <small className="text-muted">Account Name</small>
                    <div><strong>{ghlStatus.location_name}</strong></div>
                  </div>
                )}
                <div className="mb-2">
                  <small className="text-muted">Location ID</small>
                  <div><code style={{ fontSize: '0.85rem' }}>{ghlStatus.location_id}</code></div>
                </div>
                {ghlStatus.provisioned_at && (
                  <div>
                    <small className="text-muted">Provisioned</small>
                    <div>{new Date(ghlStatus.provisioned_at).toLocaleDateString()}</div>
                  </div>
                )}
              </div>
              <div className="d-flex gap-2 flex-wrap mb-3">
                <Button
                  variant="dark"
                  href="https://app.gohighlevel.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ backgroundColor: colors.greyDarkest }}
                >
                  Open GHL Dashboard
                </Button>
              </div>

              {/* Connection items */}
              <div style={{ border: `1px solid ${colors.greyLight}`, borderRadius: '8px', overflow: 'hidden' }}>
                {/* Stripe */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderBottom: `1px solid ${colors.greyLight}`,
                    backgroundColor: 'white',
                  }}
                >
                  <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>💳</span>
                  <div style={{ flex: 1 }}>
                    <strong>Connect Your Stripe Account</strong>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                      Required to accept payments for your products and services
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    href="https://app.gohighlevel.com/payments/integrations"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Connect →
                  </Button>
                </div>

                {/* Domain */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderBottom: `1px solid ${colors.greyLight}`,
                    backgroundColor: 'white',
                  }}
                >
                  <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>🌐</span>
                  <div style={{ flex: 1 }}>
                    <strong>Connect Your Domain</strong>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                      Use your own domain for funnels, landing pages, and email sending
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    href="https://app.gohighlevel.com/settings/domains"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Connect →
                  </Button>
                </div>

                {/* Email Sending */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderBottom: `1px solid ${colors.greyLight}`,
                    backgroundColor: 'white',
                  }}
                >
                  <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>✉️</span>
                  <div style={{ flex: 1 }}>
                    <strong>Set Up Email Sending</strong>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                      Add a dedicated subdomain (e.g. <code>mail.yourdomain.com</code>) so you can email your leads and customers
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    href="https://app.gohighlevel.com/settings/email-services"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Set Up →
                  </Button>
                </div>

                {/* Link Tracking */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderBottom: `1px solid ${colors.greyLight}`,
                    backgroundColor: 'white',
                  }}
                >
                  <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>🔗</span>
                  <div style={{ flex: 1 }}>
                    <strong>Set Up Link Tracking</strong>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                      Add a dedicated subdomain (e.g. <code>link.yourdomain.com</code>) for branded links in your emails and funnels
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    href="https://app.gohighlevel.com/settings/domains"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Set Up →
                  </Button>
                </div>

                {/* Socials */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'white',
                  }}
                >
                  <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>📱</span>
                  <div style={{ flex: 1 }}>
                    <strong>Connect Your Socials</strong>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                      Link Facebook, Instagram, LinkedIn, etc. for automated posting
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    href="https://app.gohighlevel.com/settings/social"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Connect →
                  </Button>
                </div>
              </div>

              <Alert variant="info" className="mt-3 mb-0 py-2">
                <small>
                  <strong>Login credentials:</strong> Check your email for your GoHighLevel login details.
                  They were sent when your account was provisioned. Use the same email address you registered with.
                </small>
              </Alert>
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted mb-2">
                Your GoHighLevel account hasn't been set up yet. This happens automatically when your
                Command Center tier is activated.
              </p>
              <Badge bg="warning" text="dark">Pending Provisioning</Badge>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Notification Preferences */}
      <h4 id="section-notifications" className="mb-3 mt-4" style={{ color: colors.greyDarkest, scrollMarginTop: '80px' }}>
        Notification Preferences
      </h4>
      <Card className="shadow-sm mb-4">
        <Card.Body>
          {/* Explanation blurb */}
          <Alert variant="info" className="mb-4">
            <strong>What reminders will you receive?</strong>
            <ul className="mb-0 mt-2">
              <li><strong>Task Reminders:</strong> Nudges about incomplete onboarding items and upcoming deadlines</li>
              <li><strong>Promotion Alerts:</strong> Heads-up before scheduled promotions (Black Friday, launches, etc.)</li>
              <li><strong>Weekly Progress:</strong> Summary of what you've accomplished and what's next</li>
              <li><strong>Strategy Tips:</strong> Occasional AI-powered suggestions based on your business data</li>
            </ul>
          </Alert>

          <p className="text-muted mb-3">
            How would you like to be reminded about tasks and deadlines?
          </p>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="reminder-email"
                  label="Email reminders"
                  checked={preferences.reminder_email}
                  onChange={(e) => setPreferences({ ...preferences, reminder_email: e.target.checked })}
                />
                <Form.Text className="text-muted ms-4">
                  Receive task reminders via email
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="reminder-sms"
                  label="SMS reminders"
                  checked={preferences.reminder_sms}
                  onChange={(e) => setPreferences({ ...preferences, reminder_sms: e.target.checked })}
                />
                <Form.Text className="text-muted ms-4">
                  Receive task reminders via text message
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {preferences.reminder_sms && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={preferences.phone_number}
                    onChange={(e) => setPreferences({ ...preferences, phone_number: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Required for SMS reminders
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row className="mt-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Your Timezone</Form.Label>
                <Form.Select
                  value={preferences.timezone}
                  onChange={(e) => setPreferences({ ...preferences, timezone: e.target.value })}
                >
                  <optgroup label="United States">
                    <option value="America/New_York">Eastern Time (New York)</option>
                    <option value="America/Chicago">Central Time (Chicago)</option>
                    <option value="America/Denver">Mountain Time (Denver)</option>
                    <option value="America/Phoenix">Arizona (Phoenix - No DST)</option>
                    <option value="America/Los_Angeles">Pacific Time (Los Angeles)</option>
                    <option value="America/Anchorage">Alaska (Anchorage)</option>
                    <option value="Pacific/Honolulu">Hawaii (Honolulu)</option>
                  </optgroup>
                  <optgroup label="Canada">
                    <option value="America/Toronto">Eastern (Toronto)</option>
                    <option value="America/Vancouver">Pacific (Vancouver)</option>
                  </optgroup>
                  <optgroup label="Europe">
                    <option value="Europe/London">UK (London)</option>
                    <option value="Europe/Paris">Central Europe (Paris)</option>
                    <option value="Europe/Berlin">Central Europe (Berlin)</option>
                  </optgroup>
                  <optgroup label="Australia">
                    <option value="Australia/Sydney">Eastern (Sydney)</option>
                    <option value="Australia/Perth">Western (Perth)</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="UTC">UTC</option>
                  </optgroup>
                </Form.Select>
                <Form.Text className="text-muted">
                  Detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Preferred Reminder Time</Form.Label>
                <Form.Select
                  value={preferences.reminder_hour}
                  onChange={(e) => setPreferences({ ...preferences, reminder_hour: e.target.value })}
                >
                  <option value="7">7:00 AM</option>
                  <option value="8">8:00 AM</option>
                  <option value="9">9:00 AM</option>
                  <option value="10">10:00 AM</option>
                  <option value="11">11:00 AM</option>
                  <option value="12">12:00 PM</option>
                  <option value="13">1:00 PM</option>
                  <option value="14">2:00 PM</option>
                  <option value="15">3:00 PM</option>
                  <option value="16">4:00 PM</option>
                  <option value="17">5:00 PM</option>
                  <option value="18">6:00 PM</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  When should we send you reminders?
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Which days can we send reminders?</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <Form.Check
                      key={day}
                      type="checkbox"
                      id={`day-${day}`}
                      label={day.charAt(0).toUpperCase() + day.slice(1, 3)}
                      checked={preferences.reminder_days.includes(day)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPreferences({ ...preferences, reminder_days: [...preferences.reminder_days, day] });
                        } else {
                          setPreferences({ ...preferences, reminder_days: preferences.reminder_days.filter(d => d !== day) });
                        }
                      }}
                      inline
                    />
                  ))}
                </div>
                <Form.Text className="text-muted">
                  We'll only send reminders on these days
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="quiet-mode"
                  label="Quiet Mode (pause all reminders)"
                  checked={preferences.quiet_mode}
                  onChange={(e) => setPreferences({ ...preferences, quiet_mode: e.target.checked })}
                />
                <Form.Text className="text-muted">
                  Going on vacation? Turn this on to pause all reminders. You can also manage this from your profile menu (click your avatar in the sidebar).
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Button
            variant="dark"
            onClick={savePreferences}
            disabled={prefsSaving}
            style={{ backgroundColor: colors.greyDarkest }}
          >
            {prefsSaving ? 'Saving...' : 'Save Preferences'}
          </Button>
          {prefsSaved && (
            <span className="text-success ms-3">
              ✓ Preferences saved!
            </span>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default OnboardingStatus;
